import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft, Calendar, Clock, User, Tag, Search, 
  ChevronRight, BookOpen, TrendingUp, Zap, Globe,
  Target, BarChart3, Bot, Sparkles, Share2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// 15 SEO-friendly blog posts
const blogPosts = [
  {
    id: 1,
    slug: 'what-is-seo-automation-complete-guide-2025',
    title: 'What is SEO Automation? Complete Guide for 2025',
    excerpt: 'Learn how AI-powered SEO automation can transform your digital marketing strategy, save 40+ hours weekly, and boost rankings automatically.',
    content: `
      <h2>Understanding SEO Automation</h2>
      <p>SEO automation uses artificial intelligence and machine learning to handle repetitive SEO tasks automatically. Instead of manually optimizing meta tags, building backlinks, and tracking rankings, AI agents perform these tasks 24/7.</p>
      
      <h2>Why SEO Automation Matters in 2025</h2>
      <p>With Google's algorithms becoming increasingly sophisticated, manual SEO is no longer sustainable. Businesses need AI-powered tools that can:</p>
      <ul>
        <li>Analyze thousands of keywords in seconds</li>
        <li>Automatically optimize content for search intent</li>
        <li>Monitor rankings and make real-time adjustments</li>
        <li>Generate SEO-optimized content at scale</li>
      </ul>
      
      <h2>Key Benefits of SEO Automation</h2>
      <p><strong>Time Savings:</strong> Save 40+ hours per week on manual SEO tasks.</p>
      <p><strong>Cost Reduction:</strong> Reduce marketing costs by up to 70%.</p>
      <p><strong>Consistent Results:</strong> AI works 24/7 without breaks or errors.</p>
      <p><strong>Data-Driven Decisions:</strong> Make decisions based on real-time analytics.</p>
      
      <h2>How AutomateSEO.ai Automates Your SEO</h2>
      <p>AutomateSEO.ai is India's 1st fully automated AI platform for SEO, AEO, GEO & Social Media Automation. Our AI agents handle everything from keyword research to content optimization automatically.</p>
    `,
    category: 'SEO',
    tags: ['SEO Automation', 'AI SEO', 'Digital Marketing', '2025 Trends'],
    author: 'AutomateSEO Team',
    date: '2025-01-15',
    readTime: '8 min read',
    image: '/blog/seo-automation.jpg'
  },
  {
    id: 2,
    slug: 'aeo-answer-engine-optimization-guide',
    title: 'AEO (Answer Engine Optimization): The Future of Search',
    excerpt: 'Discover how Answer Engine Optimization helps your content appear in featured snippets, voice search results, and AI-generated answers.',
    content: `
      <h2>What is Answer Engine Optimization (AEO)?</h2>
      <p>AEO (Answer Engine Optimization) is the practice of optimizing your content to appear directly in search engine answer boxes, featured snippets, and voice assistant responses.</p>
      
      <h2>Why AEO is Critical for Modern SEO</h2>
      <p>With the rise of voice search and AI assistants like Google Assistant, Siri, and Alexa, users increasingly expect direct answers rather than lists of links.</p>
      
      <h2>AEO Best Practices</h2>
      <ul>
        <li>Structure content with clear questions and concise answers (40-60 words)</li>
        <li>Use FAQ schema markup for better visibility</li>
        <li>Create content that directly answers user queries</li>
        <li>Optimize for natural language and conversational queries</li>
      </ul>
      
      <h2>How AutomateSEO.ai Optimizes for AEO</h2>
      <p>Our AI automatically structures your content for featured snippets and implements proper schema markup to maximize your chances of appearing in answer boxes.</p>
    `,
    category: 'AEO',
    tags: ['AEO', 'Featured Snippets', 'Voice Search', 'Schema Markup'],
    author: 'AutomateSEO Team',
    date: '2025-01-14',
    readTime: '6 min read',
    image: '/blog/aeo-guide.jpg'
  },
  {
    id: 3,
    slug: 'geo-generative-engine-optimization-chatgpt-seo',
    title: 'GEO (Generative Engine Optimization): Ranking in ChatGPT & AI Search',
    excerpt: 'Learn how to optimize your content for AI-powered search engines like ChatGPT, Google Gemini, and Perplexity to get cited in AI-generated responses.',
    content: `
      <h2>What is Generative Engine Optimization (GEO)?</h2>
      <p>GEO is the next evolution of SEO, focused on optimizing content so it gets cited by AI models like ChatGPT, Google Gemini, Claude, and Perplexity when generating responses.</p>
      
      <h2>Why GEO Matters Now</h2>
      <p>As millions of users shift from traditional search to AI assistants, businesses that don't optimize for GEO will lose significant visibility.</p>
      
      <h2>GEO Optimization Strategies</h2>
      <ul>
        <li>Create authoritative, expert-level content</li>
        <li>Include unique data, statistics, and research</li>
        <li>Build strong E-E-A-T signals (Experience, Expertise, Authoritativeness, Trust)</li>
        <li>Ensure factual accuracy with verifiable sources</li>
        <li>Use structured data and semantic markup</li>
      </ul>
      
      <h2>AutomateSEO.ai's GEO Features</h2>
      <p>Our platform automatically optimizes your content with GEO best practices, ensuring your brand gets cited when AI assistants answer relevant queries.</p>
    `,
    category: 'GEO',
    tags: ['GEO', 'ChatGPT SEO', 'AI Search', 'Generative AI'],
    author: 'AutomateSEO Team',
    date: '2025-01-13',
    readTime: '7 min read',
    image: '/blog/geo-optimization.jpg'
  },
  {
    id: 4,
    slug: 'local-seo-google-business-profile-optimization',
    title: 'Local SEO & Google Business Profile Optimization Guide',
    excerpt: 'Master local SEO and Google Business Profile optimization to dominate local search results and attract more customers to your business.',
    content: `
      <h2>Why Local SEO Matters</h2>
      <p>46% of all Google searches have local intent. If you're not optimizing for local search, you're missing nearly half of your potential customers.</p>
      
      <h2>Google Business Profile Optimization</h2>
      <ul>
        <li>Complete every section of your GBP profile</li>
        <li>Add high-quality photos and videos regularly</li>
        <li>Respond to all reviews within 24 hours</li>
        <li>Post updates, offers, and events weekly</li>
        <li>Use relevant categories and attributes</li>
      </ul>
      
      <h2>Local SEO Ranking Factors</h2>
      <p><strong>Proximity:</strong> How close your business is to the searcher</p>
      <p><strong>Relevance:</strong> How well your profile matches the search query</p>
      <p><strong>Prominence:</strong> How well-known your business is online</p>
      
      <h2>Automate Your Local SEO</h2>
      <p>AutomateSEO.ai automatically manages your Google Business Profile, posts updates, and responds to reviews - all powered by AI.</p>
    `,
    category: 'Local SEO',
    tags: ['Local SEO', 'Google Business Profile', 'GMB', 'Local Rankings'],
    author: 'AutomateSEO Team',
    date: '2025-01-12',
    readTime: '9 min read',
    image: '/blog/local-seo.jpg'
  },
  {
    id: 5,
    slug: 'social-media-automation-instagram-facebook-linkedin',
    title: 'Social Media Automation: Instagram, Facebook & LinkedIn Guide',
    excerpt: 'Learn how to automate your social media marketing across Instagram, Facebook, and LinkedIn while maintaining authentic engagement.',
    content: `
      <h2>The Power of Social Media Automation</h2>
      <p>Social media automation allows you to maintain a consistent presence across multiple platforms without spending hours each day posting and engaging.</p>
      
      <h2>What Can Be Automated?</h2>
      <ul>
        <li>Content scheduling and publishing</li>
        <li>Hashtag research and optimization</li>
        <li>Content creation with AI</li>
        <li>Analytics and reporting</li>
        <li>Cross-platform posting</li>
      </ul>
      
      <h2>Platform-Specific Strategies</h2>
      <p><strong>Instagram:</strong> Focus on visual content, Reels, and Stories</p>
      <p><strong>Facebook:</strong> Mix content types, use Groups, and leverage video</p>
      <p><strong>LinkedIn:</strong> Share thought leadership and industry insights</p>
      
      <h2>AutomateSEO.ai Social Media Features</h2>
      <p>Our AI creates, schedules, and posts content across all major platforms automatically, ensuring consistent brand presence 24/7.</p>
    `,
    category: 'Social Media',
    tags: ['Social Media Automation', 'Instagram', 'Facebook', 'LinkedIn'],
    author: 'AutomateSEO Team',
    date: '2025-01-11',
    readTime: '7 min read',
    image: '/blog/social-media.jpg'
  },
  {
    id: 6,
    slug: 'ai-content-generation-blogs-images-videos',
    title: 'AI Content Generation: Creating Blogs, Images & Videos Automatically',
    excerpt: 'Discover how AI can generate high-quality blogs, stunning images with DALL-E 3, and professional videos with RunwayML Gen-3.',
    content: `
      <h2>The AI Content Revolution</h2>
      <p>AI-powered content generation has transformed from a novelty to a necessity. Businesses using AI content tools see 3x higher content output and 50% cost savings.</p>
      
      <h2>AI Blog Writing</h2>
      <p>Modern AI can write SEO-optimized blog posts that rank on Google. The key is proper prompting and human editing for brand voice consistency.</p>
      
      <h2>AI Image Generation with DALL-E 3</h2>
      <p>Create stunning, unique images for social media, blogs, and marketing materials without hiring designers or purchasing stock photos.</p>
      
      <h2>AI Video Creation with RunwayML</h2>
      <p>Generate professional-quality videos for social media, ads, and product showcases in minutes instead of hours.</p>
      
      <h2>AutomateSEO.ai Content Suite</h2>
      <p>Our platform integrates GPT-4, DALL-E 3, and RunwayML Gen-3 to create a complete content ecosystem for your business.</p>
    `,
    category: 'AI Content',
    tags: ['AI Content', 'DALL-E 3', 'RunwayML', 'Content Generation'],
    author: 'AutomateSEO Team',
    date: '2025-01-10',
    readTime: '8 min read',
    image: '/blog/ai-content.jpg'
  },
  {
    id: 7,
    slug: 'core-web-vitals-pagespeed-optimization-guide',
    title: 'Core Web Vitals & PageSpeed Optimization: Complete Guide',
    excerpt: 'Learn how to optimize Core Web Vitals (LCP, FID, CLS) and improve PageSpeed scores for better Google rankings and user experience.',
    content: `
      <h2>What are Core Web Vitals?</h2>
      <p>Core Web Vitals are Google's metrics for measuring user experience: Largest Contentful Paint (LCP), First Input Delay (FID), and Cumulative Layout Shift (CLS).</p>
      
      <h2>Why Core Web Vitals Matter for SEO</h2>
      <p>Google uses Core Web Vitals as ranking factors. Sites with good scores rank higher and have lower bounce rates.</p>
      
      <h2>Optimization Strategies</h2>
      <ul>
        <li><strong>LCP:</strong> Optimize images, use CDN, reduce server response time</li>
        <li><strong>FID:</strong> Minimize JavaScript execution, use web workers</li>
        <li><strong>CLS:</strong> Set image dimensions, avoid inserting content above existing content</li>
      </ul>
      
      <h2>Automated Monitoring</h2>
      <p>AutomateSEO.ai continuously monitors your Core Web Vitals and PageSpeed scores, alerting you to issues before they impact rankings.</p>
    `,
    category: 'Technical SEO',
    tags: ['Core Web Vitals', 'PageSpeed', 'Technical SEO', 'LCP', 'CLS'],
    author: 'AutomateSEO Team',
    date: '2025-01-09',
    readTime: '10 min read',
    image: '/blog/core-web-vitals.jpg'
  },
  {
    id: 8,
    slug: 'google-search-console-analytics-guide',
    title: 'Google Search Console & Analytics Integration Guide',
    excerpt: 'Master Google Search Console and GA4 integration to track SEO performance, identify opportunities, and make data-driven decisions.',
    content: `
      <h2>Why Search Console Integration Matters</h2>
      <p>Google Search Console provides invaluable data about how Google sees your site, including indexing status, search queries, and technical issues.</p>
      
      <h2>Key Search Console Metrics</h2>
      <ul>
        <li>Search queries and click-through rates</li>
        <li>Page indexing status</li>
        <li>Core Web Vitals performance</li>
        <li>Mobile usability issues</li>
        <li>Security issues and manual actions</li>
      </ul>
      
      <h2>GA4 for SEO Analysis</h2>
      <p>Google Analytics 4 helps you understand user behavior, conversion paths, and which content drives the most value.</p>
      
      <h2>AutomateSEO.ai Integration</h2>
      <p>Our platform connects directly to Search Console and GA4, pulling data automatically to optimize your SEO strategy in real-time.</p>
    `,
    category: 'Analytics',
    tags: ['Google Search Console', 'GA4', 'Analytics', 'SEO Tracking'],
    author: 'AutomateSEO Team',
    date: '2025-01-08',
    readTime: '7 min read',
    image: '/blog/search-console.jpg'
  },
  {
    id: 9,
    slug: 'keyword-research-strategy-2025',
    title: 'Keyword Research Strategy for 2025: AI-Powered Approach',
    excerpt: 'Learn modern keyword research techniques using AI tools to find high-value keywords with low competition and high conversion potential.',
    content: `
      <h2>Keyword Research in the AI Era</h2>
      <p>Traditional keyword research is evolving. AI tools can now analyze search intent, predict trends, and identify opportunities humans might miss.</p>
      
      <h2>Types of Keywords to Target</h2>
      <ul>
        <li><strong>Transactional:</strong> "buy", "price", "discount"</li>
        <li><strong>Informational:</strong> "how to", "what is", "guide"</li>
        <li><strong>Navigational:</strong> Brand-specific searches</li>
        <li><strong>Commercial:</strong> "best", "top", "review"</li>
      </ul>
      
      <h2>Long-Tail Keywords</h2>
      <p>Long-tail keywords have lower search volume but higher conversion rates. Focus on specific queries your target audience uses.</p>
      
      <h2>AI-Powered Keyword Research</h2>
      <p>AutomateSEO.ai uses AI to continuously discover new keyword opportunities and automatically optimize your content for them.</p>
    `,
    category: 'SEO',
    tags: ['Keyword Research', 'SEO Strategy', 'Long-tail Keywords', 'Search Intent'],
    author: 'AutomateSEO Team',
    date: '2025-01-07',
    readTime: '8 min read',
    image: '/blog/keyword-research.jpg'
  },
  {
    id: 10,
    slug: 'on-page-seo-optimization-checklist',
    title: 'On-Page SEO Optimization: Complete Checklist for 2025',
    excerpt: 'Follow this comprehensive on-page SEO checklist to optimize your website pages for maximum search engine visibility and rankings.',
    content: `
      <h2>On-Page SEO Fundamentals</h2>
      <p>On-page SEO refers to optimizing individual web pages to rank higher and earn more relevant traffic from search engines.</p>
      
      <h2>Title Tag Optimization</h2>
      <p>Keep titles under 60 characters, include primary keyword near the beginning, and make them compelling for clicks.</p>
      
      <h2>Meta Description Best Practices</h2>
      <p>Write unique descriptions under 160 characters that include keywords and a clear call-to-action.</p>
      
      <h2>Header Tag Structure</h2>
      <ul>
        <li>Use one H1 per page with primary keyword</li>
        <li>Use H2s for main sections</li>
        <li>Use H3s for subsections</li>
        <li>Include keywords naturally</li>
      </ul>
      
      <h2>Automated On-Page SEO</h2>
      <p>AutomateSEO.ai automatically optimizes your title tags, meta descriptions, and header structure for maximum SEO impact.</p>
    `,
    category: 'SEO',
    tags: ['On-Page SEO', 'Title Tags', 'Meta Descriptions', 'Header Tags'],
    author: 'AutomateSEO Team',
    date: '2025-01-06',
    readTime: '9 min read',
    image: '/blog/on-page-seo.jpg'
  },
  {
    id: 11,
    slug: 'backlink-building-strategies-2025',
    title: 'Backlink Building Strategies That Work in 2025',
    excerpt: 'Discover ethical, effective backlink building strategies to improve domain authority and boost your search engine rankings.',
    content: `
      <h2>Why Backlinks Still Matter</h2>
      <p>Backlinks remain one of Google's top ranking factors. Quality backlinks signal authority and trustworthiness to search engines.</p>
      
      <h2>White-Hat Link Building Techniques</h2>
      <ul>
        <li>Guest posting on relevant industry sites</li>
        <li>Creating linkable assets (tools, research, infographics)</li>
        <li>Building relationships with industry influencers</li>
        <li>HARO (Help A Reporter Out) responses</li>
        <li>Broken link building</li>
      </ul>
      
      <h2>Quality Over Quantity</h2>
      <p>One backlink from a high-authority site is worth more than 100 links from low-quality sites. Focus on relevance and authority.</p>
      
      <h2>AutomateSEO.ai Backlink Features</h2>
      <p>Our Advanced plan includes premium backlink building services to boost your domain authority safely and effectively.</p>
    `,
    category: 'SEO',
    tags: ['Backlinks', 'Link Building', 'Domain Authority', 'Off-Page SEO'],
    author: 'AutomateSEO Team',
    date: '2025-01-05',
    readTime: '8 min read',
    image: '/blog/backlinks.jpg'
  },
  {
    id: 12,
    slug: 'ecommerce-seo-product-page-optimization',
    title: 'E-Commerce SEO: Product Page Optimization Guide',
    excerpt: 'Learn how to optimize your e-commerce product pages for search engines to drive more organic traffic and increase sales.',
    content: `
      <h2>E-Commerce SEO Challenges</h2>
      <p>E-commerce sites face unique SEO challenges: duplicate content, thin product descriptions, and complex site structures.</p>
      
      <h2>Product Page Optimization</h2>
      <ul>
        <li>Write unique, detailed product descriptions (300+ words)</li>
        <li>Optimize product titles with keywords</li>
        <li>Use high-quality images with alt text</li>
        <li>Implement product schema markup</li>
        <li>Include customer reviews</li>
      </ul>
      
      <h2>Category Page SEO</h2>
      <p>Category pages often have more ranking potential than individual product pages. Add unique content and optimize for category-level keywords.</p>
      
      <h2>AutomateSEO.ai for E-Commerce</h2>
      <p>Our platform automatically generates SEO-optimized product descriptions and implements proper schema markup for e-commerce sites.</p>
    `,
    category: 'E-Commerce',
    tags: ['E-Commerce SEO', 'Product Pages', 'Online Store', 'Schema Markup'],
    author: 'AutomateSEO Team',
    date: '2025-01-04',
    readTime: '9 min read',
    image: '/blog/ecommerce-seo.jpg'
  },
  {
    id: 13,
    slug: 'seo-for-startups-growth-strategy',
    title: 'SEO for Startups: Building a Growth-Focused Strategy',
    excerpt: 'Learn how startups can leverage SEO to drive sustainable growth without massive marketing budgets.',
    content: `
      <h2>Why Startups Need SEO</h2>
      <p>SEO offers startups a sustainable, cost-effective way to acquire customers. Unlike paid ads, SEO compounds over time.</p>
      
      <h2>Startup SEO Priorities</h2>
      <ul>
        <li>Focus on long-tail, low-competition keywords first</li>
        <li>Build content around customer pain points</li>
        <li>Establish topical authority in your niche</li>
        <li>Leverage founder personal brands</li>
        <li>Create comparison and alternative content</li>
      </ul>
      
      <h2>Budget-Friendly SEO Tactics</h2>
      <p>Startups can achieve significant results with limited budgets by focusing on content quality over quantity and building genuine relationships for backlinks.</p>
      
      <h2>AutomateSEO.ai for Startups</h2>
      <p>Our Starter plan at ₹2,999/month gives startups enterprise-level SEO automation without the enterprise price tag.</p>
    `,
    category: 'Business',
    tags: ['Startup SEO', 'Growth Marketing', 'Budget SEO', 'SaaS'],
    author: 'AutomateSEO Team',
    date: '2025-01-03',
    readTime: '7 min read',
    image: '/blog/startup-seo.jpg'
  },
  {
    id: 14,
    slug: 'schema-markup-structured-data-guide',
    title: 'Schema Markup & Structured Data: Complete Implementation Guide',
    excerpt: 'Learn how to implement schema markup and structured data to enhance your search listings with rich snippets and better visibility.',
    content: `
      <h2>What is Schema Markup?</h2>
      <p>Schema markup is code that helps search engines understand your content better and display rich results in search listings.</p>
      
      <h2>Types of Schema to Implement</h2>
      <ul>
        <li><strong>Organization:</strong> Company information</li>
        <li><strong>LocalBusiness:</strong> For local businesses</li>
        <li><strong>Product:</strong> E-commerce products</li>
        <li><strong>Article:</strong> Blog posts and news</li>
        <li><strong>FAQ:</strong> Frequently asked questions</li>
        <li><strong>HowTo:</strong> Step-by-step guides</li>
      </ul>
      
      <h2>Rich Results Benefits</h2>
      <p>Pages with rich snippets can see 20-30% higher click-through rates compared to standard search listings.</p>
      
      <h2>AutomateSEO.ai Schema Features</h2>
      <p>Our platform automatically implements relevant schema markup across your site for maximum rich snippet eligibility.</p>
    `,
    category: 'Technical SEO',
    tags: ['Schema Markup', 'Structured Data', 'Rich Snippets', 'Technical SEO'],
    author: 'AutomateSEO Team',
    date: '2025-01-02',
    readTime: '8 min read',
    image: '/blog/schema-markup.jpg'
  },
  {
    id: 15,
    slug: 'seo-reporting-metrics-kpis-guide',
    title: 'SEO Reporting: Essential Metrics & KPIs to Track',
    excerpt: 'Learn which SEO metrics and KPIs matter most for measuring success and making data-driven optimization decisions.',
    content: `
      <h2>Why SEO Reporting Matters</h2>
      <p>Without proper tracking and reporting, you can't know if your SEO efforts are working or where to focus improvements.</p>
      
      <h2>Essential SEO Metrics</h2>
      <ul>
        <li><strong>Organic Traffic:</strong> Visitors from search engines</li>
        <li><strong>Keyword Rankings:</strong> Position for target keywords</li>
        <li><strong>Click-Through Rate:</strong> Clicks vs. impressions</li>
        <li><strong>Bounce Rate:</strong> Single-page sessions</li>
        <li><strong>Conversion Rate:</strong> Goal completions from organic</li>
        <li><strong>Domain Authority:</strong> Overall site strength</li>
      </ul>
      
      <h2>Reporting Frequency</h2>
      <p>Track metrics weekly for quick insights, but evaluate strategy monthly. SEO results take 3-6 months to materialize.</p>
      
      <h2>AutomateSEO.ai Reports</h2>
      <p>Our platform generates comprehensive weekly SEO reports delivered via email and WhatsApp, so you always know how your site is performing.</p>
    `,
    category: 'Analytics',
    tags: ['SEO Reporting', 'KPIs', 'Metrics', 'Analytics'],
    author: 'AutomateSEO Team',
    date: '2025-01-01',
    readTime: '7 min read',
    image: '/blog/seo-reporting.jpg'
  }
];

// Get unique categories
const categories = [...new Set(blogPosts.map(post => post.category))];

const BlogPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // If slug is provided, show single post
  if (slug) {
    const post = blogPosts.find(p => p.slug === slug);
    
    if (!post) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
            <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
          </div>
        </div>
      );
    }

    // Article Schema
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt,
      "author": {
        "@type": "Organization",
        "name": "AutomateSEO.ai"
      },
      "publisher": {
        "@type": "Organization",
        "name": "AutomateSEO.ai",
        "logo": {
          "@type": "ImageObject",
          "url": "https://automateseo.ai/AutomateSEO-logo.png"
        }
      },
      "datePublished": post.date,
      "dateModified": post.date,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://automateseo.ai/blog/${post.slug}`
      }
    };

    return (
      <>
        <Helmet>
          <title>{post.title} | AutomateSEO.ai Blog</title>
          <meta name="description" content={post.excerpt} />
          <meta name="keywords" content={post.tags.join(', ')} />
          <link rel="canonical" href={`https://automateseo.ai/blog/${post.slug}`} />
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={post.excerpt} />
          <meta property="og:type" content="article" />
          <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        </Helmet>

        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
          {/* Navigation */}
          <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                <Link to="/" className="flex items-center gap-3">
                  <img src="/AutomateSEO-logo.png" alt="AutomateSEO.ai" className="h-10 w-auto" />
                  <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
                </Link>
                <Button variant="ghost" onClick={() => navigate('/blog')}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
                </Button>
              </div>
            </div>
          </nav>

          {/* Article */}
          <article className="max-w-4xl mx-auto px-4 py-12">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
              <Link to="/" className="hover:text-blue-600">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/blog" className="hover:text-blue-600">Blog</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900">{post.category}</span>
            </nav>

            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {post.category}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{post.title}</h1>
              <p className="text-xl text-gray-600 mb-6">{post.excerpt}</p>
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" /> {post.author}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> {new Date(post.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" /> {post.readTime}
                </span>
              </div>
            </header>

            {/* Content */}
            <div 
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-blue-600"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            <div className="mt-12 pt-8 border-t">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-5 h-5 text-gray-400" />
                {post.tags.map((tag, index) => (
                  <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Automate Your SEO?</h3>
              <p className="mb-6 opacity-90">Start your free 3-day trial and see how AI can transform your digital marketing.</p>
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100"
                onClick={() => navigate('/register')}
              >
                Start Free Trial <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </article>
        </div>
      </>
    );
  }

  // Filter posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <Helmet>
        <title>SEO, AEO & GEO Blog | AutomateSEO.ai</title>
        <meta name="description" content="Learn about SEO automation, Answer Engine Optimization (AEO), Generative Engine Optimization (GEO), and digital marketing strategies from AutomateSEO.ai experts." />
        <link rel="canonical" href="https://automateseo.ai/blog" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Navigation */}
        <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-3">
                <img src="/AutomateSEO-logo.png" alt="AutomateSEO.ai" className="h-10 w-auto" />
                <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
              </Link>
              <div className="flex items-center gap-4">
                <Link to="/login">
                  <Button variant="ghost">Login</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-blue-600 to-purple-600">Start Free Trial</Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
              <BookOpen className="w-4 h-4" />
              <span className="text-sm font-medium">AutomateSEO.ai Blog</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Learn SEO, AEO & GEO
            </h1>
            <p className="text-xl opacity-90 mb-8">
              Expert insights on SEO automation, Answer Engine Optimization, and Generative Engine Optimization
            </p>
            
            {/* Search */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 bg-white text-gray-900"
              />
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-8 px-4 border-b">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              <button
                onClick={() => setSelectedCategory('All')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                  selectedCategory === 'All'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Posts
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map(post => (
                <article 
                  key={post.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition cursor-pointer group"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <BookOpen className="w-16 h-16 text-white/50" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                        {post.category}
                      </span>
                      <span className="text-xs text-gray-500">{post.readTime}</span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{new Date(post.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span className="text-blue-600 font-medium group-hover:underline">Read More →</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No posts found matching your criteria.</p>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 px-4 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Stay Updated on SEO Trends</h2>
            <p className="text-gray-400 mb-8">Get the latest SEO, AEO, and GEO insights delivered to your inbox.</p>
            <Link to="/register">
              <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600">
                Start Free Trial <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t py-8 px-4">
          <div className="max-w-7xl mx-auto text-center text-gray-500">
            <p>© 2025 AutomateSEO.ai. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPage;
